import { Field, Input, Spinner } from "@fluentui/react-components";
import { useCallback, useEffect, useRef, useState } from "react";

import { teamAccessCheck } from "../../../services/api.service.js";
import { TeamLogoDropzone } from "../tabs/theme/components/team-logo-dropzone.js";

const VALIDATION_DEBOUNCE_MS = 400;
const TEAM_VALIDATION_ERROR =
  "We cannot find the team. Please verify you have given BeZoned RSC permissions.";

interface TeamValidationResult {
  channelCount: number;
  memberCount: number;
  teamName: string;
}

interface TeamCreateEnterpriseGeneralTabProps {
  disabled: boolean;
  errorMessage: string | null;
  teamId: string;
  logoErrorMessage: string | null;
  logoPreviewUrl: string | null;
  onDeleteLogo: () => void;
  onLogoErrorMessageChange: (message: string | null) => void;
  onLogoPreviewUrlChange: (url: string | null) => void;
  onSelectedLogoFileChange: (file: File | null) => void;
  onSubmit: (teamId: string, validation: TeamValidationResult) => Promise<void>;
  onCanContinueChange: (canContinue: boolean) => void;
  onRetryValidationChange: (retry: (() => void) | null) => void;
  onTeamIdChange: (teamId: string) => void;
}

const isGuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(
    value
  );

export const TeamCreateEnterpriseGeneralTab = ({
  disabled,
  errorMessage,
  teamId,
  logoErrorMessage,
  logoPreviewUrl,
  onDeleteLogo,
  onLogoErrorMessageChange,
  onLogoPreviewUrlChange,
  onSelectedLogoFileChange,
  onSubmit,
  onCanContinueChange,
  onRetryValidationChange,
  onTeamIdChange,
}: TeamCreateEnterpriseGeneralTabProps) => {
  const [hasBlurred, setHasBlurred] = useState(false);
  const [validation, setValidation] = useState<TeamValidationResult | null>(
    null
  );
  const [isChecking, setIsChecking] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const trimmedTeamId = teamId.trim();
  const localError =
    hasBlurred && !isGuid(trimmedTeamId) ? "Enter a valid Team ID" : null;
  const currentError = errorMessage ?? apiError ?? localError;
  const canContinue = Boolean(validation) && !isChecking && !localError;

  useEffect(() => {
    onCanContinueChange(canContinue);
  }, [canContinue, onCanContinueChange]);

  const handleTeamIdChange = (value: string) => {
    onTeamIdChange(value);
    setValidation(null);
    setApiError(null);
    setIsChecking(false);
  };

  useEffect(() => {
    if (!isGuid(trimmedTeamId)) {
      requestIdRef.current += 1;
      setIsChecking(false);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const timeoutId = setTimeout(() => {
      setIsChecking(true);
      void teamAccessCheck({ msTeamId: trimmedTeamId })
        .then((result) => {
          if (requestIdRef.current !== requestId) {
            return;
          }

          setValidation(result);
        })
        .catch(() => {
          if (requestIdRef.current !== requestId) {
            return;
          }

          setApiError(TEAM_VALIDATION_ERROR);
        })
        .finally(() => {
          if (requestIdRef.current === requestId) {
            setIsChecking(false);
          }
        });
    }, VALIDATION_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [trimmedTeamId]);

  const handleSubmit = async () => {
    if (!validation || isChecking || localError) {
      return;
    }

    await onSubmit(trimmedTeamId, validation);
  };

  const reloadValidation = useCallback(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setApiError(null);
    setValidation(null);
    setIsChecking(true);
    void teamAccessCheck({ msTeamId: trimmedTeamId })
      .then((result) => {
        if (requestIdRef.current === requestId) {
          setValidation(result);
        }
      })
      .catch(() => {
        if (requestIdRef.current === requestId) {
          setApiError(TEAM_VALIDATION_ERROR);
        }
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsChecking(false);
        }
      });
  }, [trimmedTeamId]);

  useEffect(() => {
    onRetryValidationChange(apiError ? reloadValidation : null);
  }, [apiError, onRetryValidationChange, reloadValidation]);

  return (
    <div className="flex w-full flex-col gap-8">
      <form
        id="enterprise-team-form"
        className="flex w-full flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <Field
          label="Microsoft Team ID"
          validationMessage={currentError}
          validationState={currentError ? "error" : "none"}
        >
          <Input
            className="w-full"
            contentAfter={isChecking ? <Spinner size="tiny" /> : null}
            disabled={disabled}
            onBlur={() => setHasBlurred(true)}
            onChange={(event) => handleTeamIdChange(event.target.value)}
            value={teamId}
          />
        </Field>

        {validation ? (
          <div className="text-bzd-gray-500 text-sm">
            You have selected: <strong>{validation.teamName}</strong>, with{" "}
            {validation.memberCount} pre-existing members and{" "}
            {validation.channelCount} existing channels.
          </div>
        ) : null}
      </form>

      <TeamLogoDropzone
        disabled={disabled}
        errorMessage={logoErrorMessage}
        onDeleteChange={onDeleteLogo}
        onErrorMessageChange={onLogoErrorMessageChange}
        onLogoPreviewUrlChange={onLogoPreviewUrlChange}
        onSelectedFileChange={onSelectedLogoFileChange}
        previewUrl={logoPreviewUrl}
      />
    </div>
  );
};
